import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { useIsomorphicLayoutEffect } from '@cian/react-utils';
import { throttle } from '../../../internal/utils';
import { cloudDisplayModeDefaultOptions } from '../../components/Tags/constants';
import { calculateNumberOfTags, restrictToPositiveNumber } from './utils';

/**
 *
 * @description Хук отвечает за расчёт количества тегов, которые нужно показать и спрятать в режиме отображения "cloud".
 * Кроме того, хук возвращает набор параметров для работы в этом режиме: хэндлер свернуть/развернуть, рефы html-элементов,
 * состояние свернутости и т.д.
 */
export function useProvideCloudDisplayModeConfig(config) {
  var _wrapperRef$current$c, _wrapperRef$current;
  const {
    tagsOptions,
    displayModeOptions,
    isCloudDisplayMode,
    wrapperRef,
    moreButtonRef,
    lessButtonRef,
    leftAdornmentRef,
    rightAdornmentRef
  } = config;
  const {
    isFoldable,
    isFolded: isFoldedInitial
  } = displayModeOptions;
  const [isFolded, setIsFolded] = React.useState(isFoldedInitial);
  const [maxLines, setMaxLines] = React.useState(cloudDisplayModeDefaultOptions.maxLines);
  const [wrapperWidth, setWrapperWidth] = React.useState((_wrapperRef$current$c = (_wrapperRef$current = wrapperRef.current) === null || _wrapperRef$current === void 0 ? void 0 : _wrapperRef$current.clientWidth) !== null && _wrapperRef$current$c !== void 0 ? _wrapperRef$current$c : 0);
  const [numberOfTagsToDisplay, setNumberOfTagsToDisplay] = React.useState(tagsOptions.length);
  const [isTagsFitMaxLines, setIsTagsFitMaxLines] = React.useState(true);
  const [tagsWidthsMap, setTagsWidthsMap] = React.useState(tagsOptions.reduce((acc, opt) => _objectSpread(_objectSpread({}, acc), {}, {
    [opt.value]: 0
  }), {}));
  const isFoldableCloudMode = isCloudDisplayMode && isFoldable;

  /**
   * Каждый конкретный компонент Tag использует этот метод для установки ширины своего контейнера.
   */
  const setTagWidth = React.useCallback((id, width) => {
    setTagsWidthsMap(prevWidths => _objectSpread(_objectSpread({}, prevWidths), {}, {
      [id]: width
    }));
  }, []);

  /**
   * Аналогично: каждый конкретный компонент Tag использует этот метод для сброса ширины своего контейнера.
   */
  const clearTagWidth = React.useCallback(id => {
    setTagsWidthsMap(prevWidths => {
      delete prevWidths[id];
      return prevWidths;
    });
  }, []);
  React.useEffect(() => {
    var _displayModeOptions$m;
    const maxLinesValue = (_displayModeOptions$m = displayModeOptions.maxLines) !== null && _displayModeOptions$m !== void 0 ? _displayModeOptions$m : cloudDisplayModeDefaultOptions.maxLines;
    setMaxLines(() => restrictToPositiveNumber(maxLinesValue));
  }, [displayModeOptions.maxLines]);

  /**
   * Устанавливаем ширину контейнера тегов на маунт.
   * Потребуется для всех расчётов количества тегов, которые нужно показать.
   */
  useIsomorphicLayoutEffect(() => {
    if (isFoldableCloudMode && wrapperRef.current) {
      setWrapperWidth(wrapperRef.current.clientWidth);
    }
  }, [isFoldableCloudMode]);

  /**
   * Основной метод, который рассчитывает количество тегов, которые нужно показать.
   */
  const updateNumberOfTagsToDisplay = React.useCallback(isFolded => {
    var _moreButtonRef$curren, _moreButtonRef$curren2, _lessButtonRef$curren, _lessButtonRef$curren2, _leftAdornmentRef$cur, _leftAdornmentRef$cur2, _rightAdornmentRef$cu, _rightAdornmentRef$cu2;
    if (!isCloudDisplayMode || !isFoldable) {
      setNumberOfTagsToDisplay(tagsOptions.length);
      return tagsOptions.length;
    }
    const {
      numberOfTags,
      isTagsFitMaxLines
    } = calculateNumberOfTags({
      isFolded,
      tagsOptions,
      maxLines,
      availableWidth: wrapperWidth,
      moreButtonWidth: (_moreButtonRef$curren = (_moreButtonRef$curren2 = moreButtonRef.current) === null || _moreButtonRef$curren2 === void 0 ? void 0 : _moreButtonRef$curren2.clientWidth) !== null && _moreButtonRef$curren !== void 0 ? _moreButtonRef$curren : 0,
      lessButtonWidth: (_lessButtonRef$curren = (_lessButtonRef$curren2 = lessButtonRef.current) === null || _lessButtonRef$curren2 === void 0 ? void 0 : _lessButtonRef$curren2.clientWidth) !== null && _lessButtonRef$curren !== void 0 ? _lessButtonRef$curren : 0,
      leftAdornmentWidth: (_leftAdornmentRef$cur = (_leftAdornmentRef$cur2 = leftAdornmentRef.current) === null || _leftAdornmentRef$cur2 === void 0 ? void 0 : _leftAdornmentRef$cur2.clientWidth) !== null && _leftAdornmentRef$cur !== void 0 ? _leftAdornmentRef$cur : 0,
      rightAdornmentWidth: (_rightAdornmentRef$cu = (_rightAdornmentRef$cu2 = rightAdornmentRef.current) === null || _rightAdornmentRef$cu2 === void 0 ? void 0 : _rightAdornmentRef$cu2.clientWidth) !== null && _rightAdornmentRef$cu !== void 0 ? _rightAdornmentRef$cu : 0,
      tagsWidthsMap
    });
    setIsTagsFitMaxLines(isTagsFitMaxLines);
    setNumberOfTagsToDisplay(numberOfTags);
    return numberOfTags;
  }, [isCloudDisplayMode, isFoldable, wrapperWidth, tagsWidthsMap, maxLines, tagsOptions, moreButtonRef, lessButtonRef, leftAdornmentRef, rightAdornmentRef]);

  /**
   * При удалении или добавлении нового тега пересчитываем количество тегов, которые нужно показать.
   */
  React.useEffect(() => {
    updateNumberOfTagsToDisplay(isFolded);
  }, [tagsWidthsMap, updateNumberOfTagsToDisplay, isFolded]);

  /**
   * Добавляем resize observer, чтобы следить за изменением ширины контейнера тегов.
   */
  useIsomorphicLayoutEffect(() => {
    let resizeObserver;
    const callback = throttle(entries => {
      for (const entry of entries) {
        setWrapperWidth(entry.target.clientWidth);
      }
    });
    if (typeof ResizeObserver !== 'undefined' && wrapperRef.current) {
      resizeObserver = new ResizeObserver(callback);
      resizeObserver.observe(wrapperRef.current);
    }
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  /**
   * Хэндлер для переключения между состояниями "свернуто" и "развернуто" в режиме отображения "cloud".
   */
  const cloudModeControlHandler = React.useCallback(() => {
    setIsFolded(isFolded => {
      const onChangeHandler = isFolded ? displayModeOptions.onShowMore : displayModeOptions.onHide;
      onChangeHandler === null || onChangeHandler === void 0 || onChangeHandler();
      updateNumberOfTagsToDisplay(!isFolded);
      return !isFolded;
    });
  }, [displayModeOptions.onShowMore, displayModeOptions.onHide, updateNumberOfTagsToDisplay]);
  const numberOfTagsHidden = tagsOptions.length - numberOfTagsToDisplay;
  return React.useMemo(() => ({
    isFolded,
    cloudModeControlHandler,
    setTagWidth,
    clearTagWidth,
    numberOfTagsToDisplay,
    numberOfTagsHidden,
    isTagsFitMaxLines
  }), [isFolded, cloudModeControlHandler, setTagWidth, clearTagWidth, numberOfTagsToDisplay, numberOfTagsHidden, isTagsFitMaxLines]);
}